// SAMPLE DATA — the deterministic generators behind the pages that have no live
// source yet: Performance › Sales (Stripe, once it is connected) and
// Performance › Software (production telemetry, error logs and heartbeat
// checks, once they are wired). Nothing here reaches a network.
//
// Two rules, both in service of the same honesty the Activity page keeps:
//
//   1. SAMPLE DATA IS STABLE. Every generator is seeded, so a reload shows the
//      same numbers as the last one and a screenshot taken today matches the
//      page tomorrow (bar for the date labels, which track the real calendar).
//      Math.random() would make the page look like a live feed that changes
//      when nobody did anything, which is exactly the impression a sample
//      must not give.
//
//   2. SAMPLE DATA SAYS SO. A snapshot built from these helpers carries
//      `mode: 'sample'`, and the page renders that as a visible label. When the
//      real source lands, the page keeps its shape and only the snapshot's
//      origin changes — the label goes with it.

// mulberry32: a small, fast 32-bit PRNG with good distribution for a UI. Returns
// a function yielding floats in [0, 1). Same seed, same sequence, forever.
export function seededRandom(seed) {
  let a = (Number(seed) >>> 0) || 1
  return function next() {
    a = (a + 0x6d2b79f5) >>> 0
    let t = a
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

// FNV-1a over a string, so a generator can be seeded with a readable name
// ("sales", "software:errors") instead of a magic number.
export function hashSeed(str) {
  let h = 0x811c9dc5
  const s = String(str ?? '')
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    h = Math.imul(h, 0x01000193) >>> 0
  }
  return h >>> 0
}

// Convenience over seededRandom: integers in [min, max], a pick from a list, a
// value jittered around a centre. All driven by one shared `next` so a caller
// gets a single reproducible stream.
export function samplers(next) {
  return {
    float: (min = 0, max = 1) => min + next() * (max - min),
    int: (min, max) => Math.floor(min + next() * (max - min + 1)),
    pick: (list) => list[Math.floor(next() * list.length)],
    // Around `centre`, ± `spread` proportionally: jitter(100, 0.2) ∈ [80, 120).
    jitter: (centre, spread = 0.2) => centre * (1 + (next() * 2 - 1) * spread),
    chance: (p) => next() < p,
  }
}

// ---- calendar helpers -----------------------------------------------------
// Local-time day keys. The sample pages are not tied to the HubSpot mirror's
// America/Chicago clock the way Activity is, so the browser's own day is the
// honest one to label bars with.

const pad2 = (n) => String(n).padStart(2, '0')

export function dayKeyOf(date) {
  const d = date instanceof Date ? date : new Date(date)
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
}

// The last `n` day keys ending on `until` (inclusive), oldest first.
export function dayKeys(n, until = new Date()) {
  const out = []
  const end = new Date(until.getFullYear(), until.getMonth(), until.getDate())
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(end)
    d.setDate(end.getDate() - i)
    out.push(dayKeyOf(d))
  }
  return out
}

// The last `n` month keys ('YYYY-MM') ending on the month of `until`, oldest
// first.
export function monthKeys(n, until = new Date()) {
  const out = []
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(until.getFullYear(), until.getMonth() - i, 1)
    out.push(`${d.getFullYear()}-${pad2(d.getMonth() + 1)}`)
  }
  return out
}

// The last `n` whole hours ending on the hour of `until`, oldest first, as
// Date objects (each on the hour).
export function hourMarks(n, until = new Date()) {
  const out = []
  const end = new Date(until)
  end.setMinutes(0, 0, 0)
  for (let i = n - 1; i >= 0; i--) {
    out.push(new Date(end.getTime() - i * 3600 * 1000))
  }
  return out
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

// 'YYYY-MM-DD' → 'Sep 3'
export function shortDay(key) {
  const [, m, d] = String(key).split('-').map(Number)
  return `${MONTHS[(m || 1) - 1]} ${d || ''}`.trim()
}

// 'YYYY-MM' → 'Sep' ('Sep ’25' when the year differs from `year`, so a
// twelve-month axis that crosses January reads unambiguously).
export function shortMonth(key, year = new Date().getFullYear()) {
  const [y, m] = String(key).split('-').map(Number)
  const name = MONTHS[(m || 1) - 1]
  return y && y !== year ? `${name} ’${String(y).slice(-2)}` : name
}

// A Date on the hour → '14:00'
export function hourLabel(date) {
  return `${pad2(date.getHours())}:00`
}

// ---- formatting -------------------------------------------------------------

// Whole-dollar money for tiles: 128400 cents → '$1,284'. Amounts under $10 keep
// their cents so a small charge is not rounded to nothing.
export function formatMoney(cents, { currency = 'USD', cents: keepCents } = {}) {
  const n = Number(cents || 0) / 100
  const showCents = keepCents ?? Math.abs(n) < 10
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: showCents ? 2 : 0,
    maximumFractionDigits: showCents ? 2 : 0,
  }).format(n)
}

// 0.0123 → '1.2%'. Null-safe: no number, no percent.
export function formatPct(frac, digits = 1) {
  if (frac == null || Number.isNaN(Number(frac))) return '—'
  return `${(Number(frac) * 100).toFixed(digits)}%`
}

// The delta object KpiShell renders: { text, dir }. `betterWhen` says which
// direction is good for THIS metric ('up' for revenue, 'down' for churn or
// errors), because the tile colours by dir and a rising error rate must not
// paint green. A prior of 0 has no meaningful ratio, so it reports flat.
export function deltaOf(current, prior, betterWhen = 'up') {
  const cur = Number(current || 0)
  const prev = Number(prior || 0)
  if (!prev) return { text: '', dir: 'flat' }
  const ratio = (cur - prev) / Math.abs(prev)
  if (Math.abs(ratio) < 0.0005) return { text: '0.0%', dir: 'flat' }
  const rising = ratio > 0
  const good = betterWhen === 'up' ? rising : !rising
  return {
    text: `${rising ? '+' : '−'}${Math.abs(ratio * 100).toFixed(1)}%`,
    dir: good ? 'up' : 'down',
  }
}

// 'x ago' for a timestamp, coarse on purpose — a sample page needs "12s ago"
// and "3h ago", not a live clock.
export function relativeAgo(iso, now = new Date()) {
  const t = new Date(iso).getTime()
  if (!Number.isFinite(t)) return '—'
  const s = Math.max(0, Math.round((now.getTime() - t) / 1000))
  if (s < 60) return `${s}s ago`
  const m = Math.round(s / 60)
  if (m < 60) return `${m}m ago`
  const h = Math.round(m / 60)
  if (h < 48) return `${h}h ago`
  return `${Math.round(h / 24)}d ago`
}

// The label every sample page shows next to its title, and the sentence
// behind it. One place, so the two pages say the same thing.
export const SAMPLE_LABEL = 'Sample data'
export function sampleNote(source) {
  return `Every number on this page is generated sample data — ${source} isn’t connected yet. The layout is what the live page will show.`
}
